import { useEffect, useMemo, useState } from 'react'
import { FormFieldVisibilityManager } from '../utils/FormFieldVisibilityManager'
import { CONDITIONAL_DISPLAY_RULES } from '../utils/patientFormConfig'

export const useFormFieldVisibility = ({
  patientDetails,
  makeAnonymousPatient,
  isForcingAnonymousPatient,
  moveEmailToMoreSettings,
  hidePatientNumber,
  isAllowedToAddUHID,
  isAllowedToAddImportId,
  hideRelationShipDetails,
  initialVisibilityConfig = {},
}) => {
  const [visibilityManager] = useState(
    () => new FormFieldVisibilityManager(initialVisibilityConfig),
  )

  const [, forceUpdate] = useState({})

  // Set up conditional visibility rules
  useEffect(() => {
    visibilityManager
      .setConditionalVisibility('mobile', (patientDetails) => !makeAnonymousPatient, [
        'makeAnonymousPatient',
      ])
      .setConditionalVisibility(
        'email',
        (patientDetails) => !makeAnonymousPatient && !moveEmailToMoreSettings,
        ['makeAnonymousPatient', 'moveEmailToMoreSettings'],
      )
      .setConditionalVisibility(
        'relationDropdown',
        (patientDetails) =>
          !hideRelationShipDetails
            ? CONDITIONAL_DISPLAY_RULES.showRelationDropdownWhen(patientDetails)
            : false,
        ['relatedToExistingPatient', 'dependentRelation'],
      )
      .setConditionalVisibility(
        'anonymousPatientCheckbox',
        (patientDetails) =>
          CONDITIONAL_DISPLAY_RULES.showAnonymousCheckboxWhen(patientDetails) &&
          !isForcingAnonymousPatient,
        ['mobile', 'email', 'relatedToExistingPatient', 'dependentRelation'],
      )
      .setConditionalVisibility('patientNumber', () => !hidePatientNumber, [])
      .setConditionalVisibility('uhid', () => isAllowedToAddUHID, [])
      .setConditionalVisibility('importId', () => isAllowedToAddImportId, [])
      .setConditionalVisibility(
        'communicationToAttendant',
        (patientDetails) =>
          CONDITIONAL_DISPLAY_RULES.showCommunicationToAttendantWhen(patientDetails?.attendants),
        ['attendants'],
      )

    // Force re-render when visibility manager is updated
    forceUpdate({})
  }, [
    hideRelationShipDetails,
    visibilityManager,
    makeAnonymousPatient,
    isForcingAnonymousPatient,
    moveEmailToMoreSettings,
    hidePatientNumber,
    isAllowedToAddUHID,
    isAllowedToAddImportId,
  ])

  // Update conditional visibility when dependencies change
  useEffect(() => {
    visibilityManager.updateConditionalVisibility(patientDetails, {
      makeAnonymousPatient,
      moveEmailToMoreSettings,
      isForcingAnonymousPatient,
    })
    forceUpdate({})
  }, [
    patientDetails,
    makeAnonymousPatient,
    moveEmailToMoreSettings,
    isForcingAnonymousPatient,
    visibilityManager,
  ])

  const isFieldVisible = useMemo(
    () => (fieldName) => visibilityManager.isFieldVisible(fieldName),
    [visibilityManager],
  )

  const hideField = (fieldName) => {
    visibilityManager.hideField(fieldName)
    forceUpdate({})
  }

  const showField = (fieldName) => {
    visibilityManager.showField(fieldName)
    forceUpdate({})
  }

  const hideFields = (fieldNames) => {
    visibilityManager.hideFields(fieldNames)
    forceUpdate({})
  }

  const showFields = (fieldNames) => {
    visibilityManager.showFields(fieldNames)
    forceUpdate({})
  }

  const hideBasicInfoFields = () => {
    visibilityManager.hideBasicInfoFields()
    forceUpdate({})
  }

  const showBasicInfoFields = () => {
    visibilityManager.showBasicInfoFields()
    forceUpdate({})
  }

  const hideMoreDetailsFields = () => {
    visibilityManager.hideMoreDetailsFields()
    forceUpdate({})
  }

  const showMoreDetailsFields = () => {
    visibilityManager.showMoreDetailsFields()
    forceUpdate({})
  }

  const hideOptionalFields = () => {
    visibilityManager.hideOptionalFields()
    forceUpdate({})
  }

  const showOptionalFields = () => {
    visibilityManager.showOptionalFields()
    forceUpdate({})
  }

  const resetVisibility = () => {
    visibilityManager.reset()
    forceUpdate({})
  }

  const updateVisibilityConfig = (config) => {
    visibilityManager.updateConfig(config)
    forceUpdate({})
  }

  const getVisibilityConfig = () => visibilityManager.getConfig()

  const getVisibleFields = () => visibilityManager.getVisibleFields()

  const getHiddenFields = () => visibilityManager.getHiddenFields()

  // Specific field visibility checks for common patterns
  const shouldShowEmailInBasicInfo = isFieldVisible('email') && !moveEmailToMoreSettings
  const shouldShowEmailInMoreDetails = isFieldVisible('email') && moveEmailToMoreSettings
  const shouldShowSearchDropdown = isFieldVisible('mobile') && !makeAnonymousPatient
  const shouldShowAnonymousCheckbox = isFieldVisible('anonymousPatientCheckbox')
  const shouldShowRelationDropdown = isFieldVisible('relationDropdown')

  return {
    // Core visibility functions
    isFieldVisible,
    hideField,
    showField,
    hideFields,
    showFields,

    // Group visibility functions
    hideBasicInfoFields,
    showBasicInfoFields,
    hideMoreDetailsFields,
    showMoreDetailsFields,
    hideOptionalFields,
    showOptionalFields,

    // Utility functions
    resetVisibility,
    updateVisibilityConfig,
    getVisibilityConfig,
    getVisibleFields,
    getHiddenFields,

    // Specific field visibility helpers
    shouldShowEmailInBasicInfo,
    shouldShowEmailInMoreDetails,
    shouldShowSearchDropdown,
    shouldShowAnonymousCheckbox,
    shouldShowRelationDropdown,

    // Access to the manager instance for advanced use cases
    visibilityManager,
  }
}
